import { Injectable, NgZone, OnDestroy, signal } from '@angular/core';
import { Observable } from 'rxjs';
import { ToastrService } from 'ngx-toastr';

export interface UserLocation {
  readonly latitude: number;
  readonly longitude: number;
  readonly accuracy: number;
  /** Browser position timestamp, in milliseconds since the Unix epoch. */
  readonly timestamp: number;
}

export class LocationAccessError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'LocationAccessError';
  }
}

const LOCATION_OPTIONS: PositionOptions = {
  enableHighAccuracy: true,
  maximumAge: 30000,
  timeout: 30000,
};

@Injectable({ providedIn: 'root' })
export class LocationService implements OnDestroy {
  private readonly position = signal<UserLocation | null>(null);
  private readonly failure = signal<LocationAccessError | null>(null);
  readonly latestLocation = this.position.asReadonly();
  readonly locationError = this.failure.asReadonly();
  private watchId: number | null = null;
  private generation = 0;

  constructor(private readonly zone: NgZone, private readonly toastr: ToastrService) {}

  getCurrentLocation(): Observable<UserLocation> {
    return new Observable<UserLocation>((subscriber) => {
      const generation = this.generation;
      try {
        this.geolocation().getCurrentPosition(
          (position) => this.zone.run(() => {
            if (subscriber.closed || generation !== this.generation) return;
            try {
              const location = this.readPosition(position);
              this.position.set(location);
              this.failure.set(null);
              subscriber.next(location);
              subscriber.complete();
            } catch (error: unknown) {
              const failure = this.normalizeError(error);
              this.recordError(failure, false);
              subscriber.error(failure);
            }
          }),
          (error) => this.zone.run(() => {
            if (subscriber.closed || generation !== this.generation) return;
            const failure = this.fromPositionError(error);
            this.recordError(failure, false);
            subscriber.error(failure);
          }),
          LOCATION_OPTIONS,
        );
      } catch (error: unknown) {
        const failure = this.normalizeError(error);
        this.recordError(failure, false);
        subscriber.error(failure);
      }
    });
  }

  startTracking(): void {
    if (this.watchId !== null) return;
    const generation = this.generation;
    try {
      this.watchId = this.geolocation().watchPosition(
        (position) => this.zone.run(() => {
          if (generation !== this.generation) return;
          try {
            this.position.set(this.readPosition(position));
            this.failure.set(null);
          } catch (error: unknown) {
            this.recordError(this.normalizeError(error), true);
          }
        }),
        (error) => this.zone.run(() => {
          if (generation !== this.generation) return;
          this.recordError(this.fromPositionError(error), true);
          if (error.code === 1) this.clearWatch();
        }),
        LOCATION_OPTIONS,
      );
    } catch (error: unknown) {
      this.recordError(this.normalizeError(error), true);
    }
  }

  stopTracking(): void {
    this.generation++;
    this.clearWatch();
    this.position.set(null);
    this.failure.set(null);
  }

  ngOnDestroy(): void {
    this.stopTracking();
  }

  private clearWatch(): void {
    if (this.watchId !== null) {
      navigator.geolocation.clearWatch(this.watchId);
      this.watchId = null;
    }
  }

  private geolocation(): Geolocation {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      throw new LocationAccessError('Location is not supported. Use a browser with location support and enable location access.');
    }
    if (typeof window !== 'undefined' && !window.isSecureContext) {
      throw new LocationAccessError('Location requires HTTPS or localhost. Open the secure site and enable location access.');
    }
    return navigator.geolocation;
  }

  private readPosition(position: GeolocationPosition): UserLocation {
    const { latitude, longitude, accuracy } = position.coords;
    const timestamp = position.timestamp;
    if (!Number.isFinite(latitude) || latitude < -90 || latitude > 90 ||
        !Number.isFinite(longitude) || longitude < -180 || longitude > 180 ||
        !Number.isFinite(accuracy) || accuracy <= 0 ||
        !Number.isFinite(timestamp) || timestamp <= 0) {
      throw new LocationAccessError('A valid location could not be received. Enable location services and try again.');
    }
    return { latitude, longitude, accuracy, timestamp };
  }

  private fromPositionError(error: GeolocationPositionError): LocationAccessError {
    switch (error.code) {
      case 1: return new LocationAccessError('Location permission was denied. Enable location permission in your browser site settings and device settings, then try again.');
      case 2: return new LocationAccessError('Your location is unavailable. Enable device location services and try again.');
      case 3: return new LocationAccessError('Getting your location timed out. Enable location services, check your GPS or network connection, and try again.');
      default: return new LocationAccessError('Unable to get your location. Enable location access and try again.');
    }
  }

  private normalizeError(error: unknown): LocationAccessError {
    return error instanceof LocationAccessError ? error : new LocationAccessError('Unable to get your location. Enable location access and try again.');
  }

  private recordError(error: LocationAccessError, notify: boolean): void {
    const changed = this.failure()?.message !== error.message;
    this.position.set(null);
    this.failure.set(error);
    if (notify && changed) this.toastr.error(error.message, 'Location required', { timeOut: 10000 });
  }
}
